// Utility functions for OCR and video processing
import Tesseract from 'tesseract.js';

export const extractTimestamps = (ocrText, fileId) => {
  if (!ocrText) return [];

  // Original pattern for well-formed timestamps
  const pattern1 = /\b\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}\b/g;
  
  // More flexible pattern to catch timestamps with missing characters or spacing issues
  const pattern2 = /\b\d{4}[-\s]?\d{2}[-\s]?\d{2}\s*\d{2}[-:\s]?\d{2}[-:\s]?\d{2}\b/g;
  
  // Pattern for timestamps with missing colons or spaces
  const pattern3 = /\b\d{4}-?\d{2}-?\d{2}\s*\d{2}\d{2}\d{2}\b/g;
  
  const matches1 = ocrText.match(pattern1) || [];
  const matches2 = ocrText.match(pattern2) || [];
  const matches3 = ocrText.match(pattern3) || [];
  
  const allMatches = [...matches1, ...matches2, ...matches3];
  
  // Remove duplicates and clean up formatting
  const cleanedMatches = [...new Set(allMatches)].map(timestamp => {
    // Clean up the timestamp format
    let cleaned = timestamp.replace(/\s+/g, ' ').trim();
    // Fix missing hyphens in date
    cleaned = cleaned.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
    // Fix missing hyphens/colons in time
    cleaned = cleaned.replace(/(\d{2})(\d{2})(\d{2})(?=\b)/, '$1:$2:$3');
    // Ensure proper spacing between date and time
    cleaned = cleaned.replace(/(\d{4}-\d{2}-\d{2})(\d{2}:)/, '$1 $2');
    return cleaned;
  });
  
  // Filter to only valid timestamps that match our required format and validate year range
  const validTimestamps = cleanedMatches.filter(timestamp => {
    // Check format first
    if (!/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(timestamp)) {
      return false;
    }
    
    // Extract year and validate range
    const year = parseInt(timestamp.substring(0, 4));
    const currentYear = new Date().getFullYear();
    
    // Allow years from 1960 to next year (to account for potential timezone differences)
    return year >= 1960 && year <= (currentYear + 1);
  });
  
  return validTimestamps;
};

// Helper function to check if timestamps are acceptable
export const areTimestampsAcceptable = (timestamps) => {
  // If no timestamps found, they are not acceptable
  if (!timestamps || timestamps.length === 0) {
    return false;
  }
  
  // Check if all timestamps are within the acceptable range (1960 to next year)
  const currentYear = new Date().getFullYear();
  const acceptable = timestamps.every(timestamp => {
    // Check format first
    if (!/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(timestamp)) {
      return false;
    }
    
    // Extract year and validate range
    const year = parseInt(timestamp.substring(0, 4));
    return year >= 1960 && year <= (currentYear + 1);
  });
  
  return acceptable;
};

export const performOCR = async (fileId, videoFiles, setOcrProcessing, setOcrRetryCount, setOcrProgress, setOcrLog, setOcrResults, setExtractedTimestamps, setVideoThumbnails, generateThumbnailAtTime, setOcrCompleted, preprocessOnRetry = false) => {
  // Find the file object
  const fileObj = videoFiles.find(item => item && item.id === fileId);
  if (!fileObj || !fileObj.file) {
    setOcrLog(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message: `Error: Unable to locate file for processing`
    }]);
    return;
  }
  
  // Add log entry
  setOcrLog(prev => [...prev, {
    timestamp: new Date().toLocaleTimeString(),
    message: `Starting timestamp extraction for ${fileObj.file.name}${preprocessOnRetry ? ' (enhanced analysis)' : ''}`
  }]);
  
  // Set processing state at the beginning
  setOcrProcessing(prev => ({ ...prev, [fileId]: true }));
  
  // Reset retry count and progress for this file if not in retry mode
  if (!preprocessOnRetry) {
    setOcrRetryCount(prev => ({ ...prev, [fileId]: 0 }));
    setOcrProgress(prev => ({ ...prev, [fileId]: { current: 0, total: 5, status: 'Preparing analysis...' } }));
  }
  
  // Try up to 5 times with intervals based on video duration
  for (let attempt = 0; attempt < 5; attempt++) {
    try {
      // Add log entry
      setOcrLog(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `Executing analysis pass ${attempt + 1}/5 for ${fileObj.file.name}`
      }]);
      
      // Update retry count in state
      setOcrRetryCount(prev => ({ ...prev, [fileId]: attempt }));
      setOcrProgress(prev => ({ 
        ...prev, 
        [fileId]: { 
          current: attempt + 1, 
          total: 5, 
          status: `Analyzing content (${attempt + 1}/5)${preprocessOnRetry ? ' (enhanced)' : ''}` 
        } 
      }));
      
      // Calculate seek time based on video duration (1/5 of video length for each attempt)
      let timeInSeconds = 0;
      if (attempt > 0) {
        // For attempts 1-4, seek to 1/5, 2/5, 3/5, 4/5 of video duration
        const videoDuration = await getVideoDuration(fileObj.file);
        timeInSeconds = (videoDuration / 5) * attempt;
        
        // Add log entry
        setOcrLog(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: `Sampling video at ${timeInSeconds.toFixed(2)}s for ${fileObj.file.name}`
        }]);
      }
      
      setOcrProgress(prev => ({ 
        ...prev, 
        [fileId]: { 
          current: attempt + 1, 
          total: 5, 
          status: `Extracting frame (${attempt + 1}/5)${preprocessOnRetry ? ' (enhanced)' : ''}` 
        } 
      }));
      
      let thumbnailDataUrl;
      
      try {
        // Add log entry
        setOcrLog(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: `Capturing frame at ${timeInSeconds.toFixed(2)}s from ${fileObj.file.name}`
        }]);
        
        thumbnailDataUrl = await generateThumbnailAtTime(fileObj.file, timeInSeconds);
      } catch (err) {
        // Add log entry
        setOcrLog(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: `Frame capture failed for ${fileObj.file.name}: ${err.message}`
        }]);
        
        setOcrResults(prev => ({
          ...prev,
          [fileId]: 'Frame capture unsuccessful'
        }));
        setExtractedTimestamps(prev => ({
          ...prev,
          [fileId]: []
        }));
        break; // Exit the loop on thumbnail generation failure
      }
      
      // Update the thumbnail in state
      setVideoThumbnails(prev => ({
        ...prev,
        [fileId]: thumbnailDataUrl
      }));
      
      setOcrProgress(prev => ({ 
        ...prev, 
        [fileId]: { 
          current: attempt + 1, 
          total: 5, 
          status: `Processing frame (${attempt + 1}/5)${preprocessOnRetry ? ' (enhanced)' : ''}` 
        } 
      }));
      
      // Create a canvas to crop the south-east corner
      const img = new Image();
      img.src = thumbnailDataUrl;
      
      // Wait for image to load
      await new Promise((resolve, reject) => {
        img.onload = () => {
          // Check if image has valid dimensions
          if (img.width === 0 || img.height === 0) {
            reject(new Error('Loaded image has invalid dimensions (0x0)'));
            return;
          }
          resolve();
        };
        img.onerror = (err) => reject(new Error(`Failed to load image for OCR processing: ${err.message}`));
      });
      
      // Create canvas and context
      const canvas = document.createElement('canvas');
      canvas.setAttribute('willReadFrequently', 'true');
      const ctx = canvas.getContext('2d');

      // Calculate dimensions for the south-east corner (50% of width and height)
      let cropWidth = Math.floor(img.width * 0.5);
      let cropHeight = Math.floor(img.height * 0.5);
      let cropX = img.width - cropWidth;
      let cropY = img.height - cropHeight;

      // Ensure minimum dimensions to prevent Tesseract errors
      const minDimension = 20; // Increased minimum to ensure reliable OCR
      if (cropWidth < minDimension || cropHeight < minDimension) {
        // If cropped area is too small, adjust to maintain minimum dimensions
        if (img.width < minDimension || img.height < minDimension) {
          // If the image itself is too small, use the whole image
          cropX = 0;
          cropY = 0;
          cropWidth = img.width;
          cropHeight = img.height;
          setOcrLog(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `Video frame too small for cropping (${img.width}x${img.height}), using full frame for OCR.`
          }]);
        } else {
          // Adjust the crop to maintain minimum dimensions while keeping it in the SE corner
          cropWidth = Math.max(minDimension, cropWidth);
          cropHeight = Math.max(minDimension, cropHeight);
          // Adjust x and y to maintain position in the SE corner but maintain minimums
          cropX = Math.max(0, img.width - cropWidth);
          cropY = Math.max(0, img.height - cropHeight);

          setOcrLog(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `Adjusted crop area to minimum size (${cropWidth}x${cropHeight}) for reliable OCR.`
          }]);
        }
      }

      // Set canvas dimensions to match final crop area
      canvas.width = cropWidth;
      canvas.height = cropHeight;

      // Draw only the cropped area of the image
      ctx.drawImage(
        img,
        cropX, cropY, cropWidth, cropHeight,  // Source rectangle (potentially adjusted crop)
        0, 0, cropWidth, cropHeight           // Destination rectangle (full canvas)
      );
      
      setOcrProgress(prev => ({ 
        ...prev, 
        [fileId]: { 
          current: attempt + 1, 
          total: 5, 
          status: `Enhancing frame (${attempt + 1}/5)${preprocessOnRetry ? ' (enhanced)' : ''}` 
        } 
      }));
      
      let preprocessedImageDataUrl;
      if (preprocessOnRetry) {
        // Add log entry
        setOcrLog(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: `Applying advanced enhancement techniques for ${fileObj.file.name}`
        }]);
        
        // Preprocess the image to improve OCR quality when in retry mode
        preprocessedImageDataUrl = preprocessImageForOCR(canvas);
      } else {
        // Using the original image data URL instead of preprocessed for better OCR
        preprocessedImageDataUrl = canvas.toDataURL('image/png');
      }
      
      setOcrProgress(prev => ({ 
        ...prev, 
        [fileId]: { 
          current: attempt + 1, 
          total: 5, 
          status: `Extracting timestamps (${attempt + 1}/5)${preprocessOnRetry ? ' (enhanced)' : ''}` 
        } 
      }));
      
      // Add log entry
      setOcrLog(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `Performing text recognition on ${fileObj.file.name}`
      }]);
      
      // Check if Tesseract is properly available
      if (!Tesseract) {
        throw new Error('Text recognition engine not available');
      }
      
      // Use the appropriate image data based on whether we're preprocessing or not
      const imageDataForOCR = preprocessOnRetry
        ? preprocessImageForOCR(canvas)
        : canvas.toDataURL('image/png');

      let result;
      try {
        result = await Tesseract.recognize(
          imageDataForOCR,
          'eng'
        );
      } catch (tesseractError) {
        // Handle Tesseract errors more gracefully
        if (tesseractError.message && tesseractError.message.includes('Image too small')) {
          throw new Error(`OCR failed due to image size issues. Try uploading a video with higher resolution or different timestamp positioning. Original error: ${tesseractError.message}`);
        }
        throw new Error(`Text recognition failed: ${tesseractError.message}`);
      }
      
      const ocrText = result.data.text.trim();
      
      // Check if OCR text is valid
      if (!ocrText || ocrText.length === 0) {
        setOcrResults(prev => ({
          ...prev,
          [fileId]: 'Text recognition completed but no text was detected'
        }));
        
        // Add log entry
        setOcrLog(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: `No text detected in ${fileObj.file.name}`
        }]);
      } else {
        setOcrResults(prev => ({
          ...prev,
          [fileId]: ocrText
        }));
      }
      
      setOcrProgress(prev => ({ 
        ...prev, 
        [fileId]: { 
          current: attempt + 1, 
          total: 5, 
          status: `Identifying timestamps (${attempt + 1}/5)${preprocessOnRetry ? ' (enhanced)' : ''}` 
        } 
      }));
      
      // Extract timestamps from the OCR text
      const timestamps = extractTimestamps(ocrText, fileId);
      
      // Check if timestamps are acceptable (within valid date range)
      const timestampsAcceptable = areTimestampsAcceptable(timestamps);
      
      // If timestamps found and are acceptable, adjust them by subtracting the seek time and dropping seconds
      if (timestamps.length > 0 && timestampsAcceptable) {
        // Parse and adjust timestamps
        const adjustedTimestamps = timestamps.map(timestamp => {
          // Parse the timestamp (YYYY-MM-DD HH:MM:SS)
          const parts = timestamp.split(' ');
          const datePart = parts[0];
          const timePart = parts[1];
          
          // Split time into components
          const [hours, minutes, seconds] = timePart.split(':').map(Number);
          
          // Convert to total seconds
          let totalSeconds = hours * 3600 + minutes * 60 + seconds;
          
          // Subtract the seek time
          totalSeconds -= timeInSeconds;
          
          // Handle negative time (if timestamp is before the seek point)
          if (totalSeconds < 0) {
            totalSeconds = 0;
          }
          
          // Convert back to HH:MM format (dropping seconds)
          const adjustedHours = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
          const adjustedMinutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
          
          // Return timestamp in YYYY-MM-DD HH:MM format (without seconds)
          return `${datePart} ${adjustedHours}:${adjustedMinutes}`;
        });
        
        setExtractedTimestamps(prev => ({
          ...prev,
          [fileId]: adjustedTimestamps
        }));
        
        setOcrRetryCount(prev => ({ ...prev, [fileId]: 0 }));
        setOcrProcessing(prev => ({ ...prev, [fileId]: false }));
        setOcrCompleted(prev => ({ ...prev, [fileId]: true })); // Mark as completed
        setOcrProgress(prev => ({ 
          ...prev, 
          [fileId]: { 
            current: 5, 
            total: 5, 
            status: 'Analysis complete' 
          } 
        }));
        
        // Add log entry
        setOcrLog(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: `Identified ${timestamps.length} timestamp(s) in ${fileObj.file.name}`
        }]);
        
        return;
      }
      
      // If this was the last attempt, or if timestamps were found but are not acceptable, update the message
      if (attempt === 4 || (timestamps.length > 0 && !timestampsAcceptable)) {
        // If we haven't tried preprocessing yet and either no timestamps were found or they are not acceptable, try preprocessing
        if (!preprocessOnRetry) {
          setOcrProgress(prev => ({ 
            ...prev, 
            [fileId]: { 
              current: 0, 
              total: 5, 
              status: 'Retrying with enhanced analysis...' 
            } 
          }));
          
          // Add log entry
          setOcrLog(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `Analysis results unsatisfactory for ${fileObj.file.name}. Initiating enhanced analysis.`
          }]);
          
          // Wait a bit before retrying with preprocessing
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Retry with preprocessing
          await performOCR(fileId, videoFiles, setOcrProcessing, setOcrRetryCount, setOcrProgress, setOcrLog, setOcrResults, setExtractedTimestamps, setVideoThumbnails, generateThumbnailAtTime, setOcrCompleted, true);
          return;
        } else {
          // If we've already tried preprocessing and still don't have acceptable timestamps
          setOcrResults(prev => ({
            ...prev,
            [fileId]: 'Analysis complete. No acceptable timestamps identified.'
          }));
          setExtractedTimestamps(prev => ({
            ...prev,
            [fileId]: []
          }));
          setOcrProgress(prev => ({ 
            ...prev, 
            [fileId]: { 
              current: 5, 
              total: 5, 
              status: 'Analysis complete. No acceptable timestamps found.' 
            } 
          }));
          
          // Add log entry
          setOcrLog(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `Analysis complete for ${fileObj.file.name}. No acceptable timestamps identified.`
          }]);
        }
      }
      
      // Wait a bit before next attempt to allow UI to update
      if (attempt < 4) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (err) {
      // Add log entry
      setOcrLog(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `Analysis error for ${fileObj.file.name}: ${err.message}`
      }]);
      
      setOcrResults(prev => ({
        ...prev,
        [fileId]: 'Analysis encountered an error. Retrying...'
      }));
      setExtractedTimestamps(prev => ({
        ...prev,
        [fileId]: []
      }));
      
      // If this is the 5th attempt and we haven't tried preprocessing yet, try preprocessing
      if (attempt === 4 && !preprocessOnRetry) {
        setOcrProgress(prev => ({ 
          ...prev, 
          [fileId]: { 
            current: 0, 
            total: 5, 
            status: 'Retrying with enhanced analysis...' 
          } 
        }));
        
        // Add log entry
        setOcrLog(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: `Initial analysis unsuccessful for ${fileObj.file.name}. Initiating enhanced analysis.`
        }]);
        
        // Wait a bit before retrying with preprocessing
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Retry with preprocessing
        await performOCR(fileId, videoFiles, setOcrProcessing, setOcrRetryCount, setOcrProgress, setOcrLog, setOcrResults, setExtractedTimestamps, setVideoThumbnails, generateThumbnailAtTime, setOcrCompleted, true);
        return;
      }
      
      break; // Exit the loop on OCR failure
    }
  }
  
  // Ensure processing state is set to false at the end
  setOcrProcessing(prev => ({ ...prev, [fileId]: false }));
  setOcrCompleted(prev => ({ ...prev, [fileId]: true })); // Mark as completed even if failed

  // Restore the first frame thumbnail after OCR processing
  try {
    const firstFrameThumbnail = await generateThumbnailAtTime(fileObj.file, 0);
    setVideoThumbnails(prev => ({
      ...prev,
      [fileId]: firstFrameThumbnail
    }));
  } catch (error) {
    // If restoring the first frame fails, log but continue
    setOcrLog(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message: `Could not restore first frame thumbnail for ${fileObj.file.name}: ${error.message}`
    }]);
  }

  // Add log entry
  setOcrLog(prev => [...prev, {
    timestamp: new Date().toLocaleTimeString(),
    message: `Analysis process finished for ${fileObj.file.name}`
  }]);
};

// Cache for video durations to prevent multiple blob URL creation
const videoDurationCache = new Map();

export const getVideoDuration = (file) => {
  // Check if file exists
  if (!file) {
    return Promise.reject(new Error('No file provided'));
  }

  // Create a unique key for the file based on name and size
  const fileKey = `${file.name}-${file.size}-${file.lastModified}`;

  // Check if duration is already cached
  if (videoDurationCache.has(fileKey)) {
    return Promise.resolve(videoDurationCache.get(fileKey));
  }

  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    const objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;

    video.addEventListener('loadedmetadata', () => {
      // Cache the duration
      videoDurationCache.set(fileKey, video.duration);
      URL.revokeObjectURL(objectUrl);
      resolve(video.duration);
    });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to get video duration'));
    });
  });
};

export const preprocessImageForOCR = (canvas) => {
  const ctx = canvas.getContext('2d');
  const width = canvas.width;
  const height = canvas.height;
  
  // Get image data
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Step 1: Convert to grayscale using luminance formula
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    
    // Convert to grayscale using luminance formula
    const gray = 0.299 * r + 0.587 * g + 0.114 * b;
    data[i] = gray;
    data[i + 1] = gray;
    data[i + 2] = gray;
  }
  
  // Put the grayscale image data back to canvas
  ctx.putImageData(imageData, 0, 0);
  
  // Step 2: Apply Gaussian blur to reduce noise
  applyGaussianBlur(ctx, width, height);
  
  // Step 3: Apply threshold to create binary image
  applyThreshold(ctx, width, height, 128);
  
  // Step 4: Apply morphological operations (optional)
  applyMorphologicalOperations(ctx, width, height);
  
  // Return the processed image as data URL
  return canvas.toDataURL('image/png');
};

export const applyGaussianBlur = (ctx, width, height) => {
  // Simple box blur as approximation of Gaussian blur
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const output = new Uint8ClampedArray(data.length);
  
  // 3x3 box blur kernel
  const kernel = [
    [1, 1, 1],
    [1, 1, 1],
    [1, 1, 1]
  ];
  const kernelWeight = 9;
  
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0;
      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const nx = Math.min(width - 1, Math.max(0, x + kx));
          const ny = Math.min(height - 1, Math.max(0, y + ky));
          const idx = (ny * width + nx) * 4;
          sum += data[idx] * kernel[ky + 1][kx + 1];
        }
      }
      const idx = (y * width + x) * 4;
      output[idx] = output[idx + 1] = output[idx + 2] = Math.round(sum / kernelWeight);
      output[idx + 3] = data[idx + 3]; // Preserve alpha
    }
  }
  
  // Copy output back to imageData
  imageData.data.set(output);
  ctx.putImageData(imageData, 0, 0);
};

export const applyThreshold = (ctx, width, height, threshold) => {
  const imageData = ctx.getImageData(0, 0, width, height);
  ctx.setAttribute('willReadFrequently', 'true'); 
  const data = imageData.data;
  
  for (let i = 0; i < data.length; i += 4) {
    const gray = data[i];
    const binary = gray > threshold ? 255 : 0;
    data[i] = data[i + 1] = data[i + 2] = binary;
  }
  
  ctx.putImageData(imageData, 0, 0);
};

export const applyMorphologicalOperations = (ctx, width, height) => {
  // Optional: Apply morphological operations like erosion or dilation
  // This is a simplified version and might not be needed for all cases
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const output = new Uint8ClampedArray(data.length);
  
  // Simple erosion operation
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      let isBlack = data[idx] === 0;
      
      // Check neighbors
      if (isBlack && x > 0 && y > 0 && x < width - 1 && y < height - 1) {
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const nx = x + kx;
            const ny = y + ky;
            const nIdx = (ny * width + nx) * 4;
            if (data[nIdx] === 255) {
              isBlack = false;
              break;
            }
          }
          if (!isBlack) break;
        }
      }
      
      output[idx] = output[idx + 1] = output[idx + 2] = isBlack ? 0 : 255;
      output[idx + 3] = data[idx + 3]; // Preserve alpha
    }
  }
  
  // Copy output back to imageData
  imageData.data.set(output);
  ctx.putImageData(imageData, 0, 0);
};